import api from './api';
import type { PartnerRequest, PaginatedResponse, RequestStatus, RequestType, Runtime } from '../types';

interface ListRequestsParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status?: RequestStatus;
}

interface CreateRequestInput {
  customerId?: string;
  type: RequestType;
  details: {
    productId?: string;
    licenseId?: string;
    deviceCount?: number;
    runtime?: Runtime;
    notes?: string;
  };
}

interface ProcessRequestInput {
  status: RequestStatus;
  response?: {
    licenseId?: string;
    notes?: string;
    newPartnerId?: string;
  };
}

export async function listRequests(params: ListRequestsParams = {}): Promise<PaginatedResponse<PartnerRequest>> {
  const response = await api.get<PaginatedResponse<PartnerRequest>>('/requests', { params });
  return response.data;
}

export async function getMyRequests(params: ListRequestsParams = {}): Promise<PaginatedResponse<PartnerRequest>> {
  const response = await api.get<PaginatedResponse<PartnerRequest>>('/requests/my', { params });
  return response.data;
}

export async function getUnreadRequestCount(since: string): Promise<number> {
  const response = await api.get<{ count: number }>('/requests/unread', { params: { since } });
  return response.data.count;
}

export async function getRequest(id: string): Promise<PartnerRequest> {
  const response = await api.get<PartnerRequest>(`/requests/${id}`);
  return response.data;
}

export async function createRequest(data: CreateRequestInput): Promise<PartnerRequest> {
  const response = await api.post<PartnerRequest>('/requests', data);
  return response.data;
}

export async function processRequest(id: string, data: ProcessRequestInput): Promise<PartnerRequest> {
  const response = await api.put<PartnerRequest>(`/requests/${id}`, data);
  return response.data;
}

export async function forwardRequest(id: string, message?: string): Promise<PartnerRequest> {
  const response = await api.post<PartnerRequest>(`/requests/${id}/forward`, { message });
  return response.data;
}

export async function addRequestComment(id: string, message: string, internal?: boolean): Promise<PartnerRequest> {
  const response = await api.post<PartnerRequest>(`/requests/${id}/comments`, { message, internal });
  return response.data;
}

export async function cancelRequest(id: string): Promise<void> {
  await api.put(`/requests/${id}/cancel`);
}
